import React, { useRef, useEffect, useCallback } from 'react';
import { MapNode } from './useMapLayout';
import {
  CLUSTER_POSITIONS,
  LOD_CLUSTER,
  LOD_NODES,
  NODE_COLORS,
  PILLAR_COLORS,
  NodeType,
  computeClusterPositions,
  getTagColor,
} from './constants';

interface Transform {
  x: number;
  y: number;
  scale: number;
}

interface MapCanvasProps {
  nodes: MapNode[];
  transform: Transform;
  selectedNodeId: string | null;
  needsRecompile: boolean;
  darkMode: boolean;
  onNodeDoubleClick: (node: MapNode) => void;
  onNodeClick: (node: MapNode) => void;
  onNodeHover?: (node: MapNode | null) => void;
  onTransformChange: (t: Transform) => void;
  onCanvasSize?: (w: number, h: number) => void;
}

// ── Edge definitions ──────────────────────────────────────────────────────────
const EDGES: Array<{ from: NodeType; to: NodeType; style: 'solid' | 'dashed' | 'dotted'; width: number }> = [
  { from: 'identity_cluster',   to: 'system_prompt', style: 'solid',  width: 2.5 },
  { from: 'controls_cluster',   to: 'system_prompt', style: 'solid',  width: 1.5 },
  { from: 'probe_cluster',      to: 'system_prompt', style: 'dashed', width: 1   },
  { from: 'capability_cluster', to: 'system_prompt', style: 'dotted', width: 1   },
  { from: 'lineage_cluster',    to: 'system_prompt', style: 'dotted', width: 0.8 },
  { from: 'controller_prompt',  to: 'system_prompt', style: 'solid',  width: 1.5 },
];

// Domain color lookup for canvas drawing (mirrors getDomainColor from colorUtils)
const DOMAIN_COLORS_CANVAS: Record<string, string> = {
  cognitive:       '#3b82f6',
  communicative:   '#a855f7',
  technical:       '#6366f1',
  creative:        '#ec4899',
  interpersonal:   '#10b981',
  domain_specific: '#06b6d4',
  ethical:         '#22c55e',
  strategic:       '#f59e0b',
  analytical:      '#3b82f6',
  meta_cognitive:  '#8b5cf6',
};

function getDomainColorCanvas(domain: string): string {
  return DOMAIN_COLORS_CANVAS[domain] ?? '#06b6d4';
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function drawHexagon(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function drawDiamond(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.8, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r * 0.8, cy);
  ctx.closePath();
}

function drawTriangleDown(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(cx - r, cy - r * 0.7);
  ctx.lineTo(cx + r, cy - r * 0.7);
  ctx.lineTo(cx, cy + r * 0.7);
  ctx.closePath();
}

function drawProbeCluster(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, radius, color } = node;

  ctx.save();
  ctx.strokeStyle = selected ? '#ffffff' : color;
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.fillStyle = darkMode ? hexToRgba(color, 0.12) : hexToRgba(color, 0.08);
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Cluster label above ring
  ctx.font = 'bold 10px system-ui, sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(node.label, x, y - radius - 4);

  // Inner probe rings — mid zoom, not when HTML overlay is active
  if (scale >= 0.45 && scale < 2.0 && node.innerProbes) {
    const DOT_R = Math.max(3, Math.min(8, scale * 9));
    const runMax = Math.max(1, ...node.innerProbes.map(ip => ip.data.run_history?.length ?? 0));

    node.innerProbes.forEach((ip) => {
      const px = x + ip.dx;
      const py = y + ip.dy;
      const probe = ip.data;
      const runCount = probe.run_history?.length ?? 0;

      const passed  = probe.last_passed === true;
      const failed  = probe.last_passed === false;
      const untested = !passed && !failed;

      const statusColor =
        passed  ? '#22c55e' :
        failed  ? '#ef4444' : '#6b7280';

      // Outer ring track
      ctx.strokeStyle = darkMode ? '#374151' : '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px, py, DOT_R, 0, Math.PI * 2);
      ctx.stroke();

      // Status arc — full circle for pass/fail, empty for untested
      if (!untested) {
        ctx.strokeStyle = statusColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py, DOT_R, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2);
        ctx.stroke();
      }

      // Center fill — tinted by status
      const fillAlpha = untested ? 0.15 : 0.35;
      ctx.fillStyle = hexToRgba(statusColor, fillAlpha);
      ctx.beginPath();
      ctx.arc(px, py, DOT_R - 2, 0, Math.PI * 2);
      ctx.fill();

      // Pass/fail glyph inside dot (only when large enough)
      if (DOT_R >= 5) {
        ctx.font = `bold ${Math.round(DOT_R * 1.1)}px system-ui, sans-serif`;
        ctx.fillStyle = passed ? '#22c55e' : failed ? '#ef4444' : (darkMode ? '#6b7280' : '#9ca3af');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(passed ? '✓' : failed ? '✗' : '○', px, py);
      }

      // Run count arc — thin outer ring showing how many runs (relative to max)
      if (runCount > 0 && scale >= 0.6) {
        const runPct = runCount / runMax;
        ctx.strokeStyle = hexToRgba('#3b82f6', 0.55);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px, py, DOT_R + 3, -Math.PI / 2, -Math.PI / 2 + runPct * Math.PI * 2);
        ctx.stroke();
      }

      // Label below dot
      if (scale >= 0.6) {
        ctx.font = '7px system-ui, sans-serif';
        ctx.fillStyle = darkMode ? '#d1d5db' : '#374151';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const short = ip.label.length > 14 ? ip.label.slice(0, 14) + '…' : ip.label;
        ctx.fillText(short, px, py + DOT_R + 4);
      }

      // Focus area accent dot (top-right)
      if (scale >= 0.7 && probe.focus_area) {
        const focusColors: Record<string, string> = {
          tethic_violation:    '#ef4444',
          antipattern_trigger: '#f97316',
          domain_edge_case:    '#3b82f6',
          tone_stress:         '#a855f7',
          capability_boundary: '#6366f1',
          safety:              '#22c55e',
        };
        ctx.fillStyle = focusColors[probe.focus_area] ?? '#6b7280';
        ctx.beginPath();
        ctx.arc(px + DOT_R * 0.7, py - DOT_R * 0.7, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // Pass/fail summary bar at bottom of cluster
    if (scale >= 0.55) {
      const total    = node.innerProbes.length;
      const passed   = node.innerProbes.filter(ip => ip.data.last_passed === true).length;
      const failed   = node.innerProbes.filter(ip => ip.data.last_passed === false).length;
      const untested = total - passed - failed;

      const barW = radius * 1.1;
      const barH = 5;
      const bx   = x - barW / 2;
      const by   = y + radius - 14;

      // Track
      ctx.fillStyle = darkMode ? '#374151' : '#e5e7eb';
      ctx.beginPath();
      ctx.roundRect(bx, by, barW, barH, 2);
      ctx.fill();

      // Passed segment (green)
      if (passed > 0) {
        ctx.fillStyle = '#22c55e';
        ctx.beginPath();
        ctx.roundRect(bx, by, barW * (passed / total), barH, 2);
        ctx.fill();
      }

      // Failed segment (red) — stacked after passed
      if (failed > 0) {
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.roundRect(bx + barW * (passed / total), by, barW * (failed / total), barH, 0);
        ctx.fill();
      }

      // Summary label
      ctx.font = '7px system-ui, sans-serif';
      ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const label = untested === total
        ? `${total} untested`
        : `${passed}✓ ${failed}✗${untested > 0 ? ` ${untested}?` : ''}`;
      ctx.fillText(label, x, by + barH + 2);
    }
  }

  ctx.restore();
}

function drawParallelogram(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  w: number,
  h: number,
  skew: number,
) {
  ctx.beginPath();
  ctx.moveTo(cx - w / 2 + skew, cy - h / 2);
  ctx.lineTo(cx + w / 2 + skew, cy - h / 2);
  ctx.lineTo(cx + w / 2 - skew, cy + h / 2);
  ctx.lineTo(cx - w / 2 - skew, cy + h / 2);
  ctx.closePath();
}

function drawIdentityCluster(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, color } = node;
  const paraW  = node.paraW  ?? 180;
  const paraH  = node.paraH  ?? 130;
  const paraSkew = node.paraSkew ?? 16;

  ctx.save();

  // ── Outer parallelogram ───────────────────────────────────────────────────
  ctx.strokeStyle = selected ? '#ffffff' : color;
  ctx.lineWidth   = selected ? 2.5 : 1.5;
  ctx.fillStyle   = darkMode ? hexToRgba(color, 0.10) : hexToRgba(color, 0.06);
  drawParallelogram(ctx, x, y, paraW, paraH, paraSkew);
  ctx.fill();
  ctx.stroke();

  // ── Cluster label above ───────────────────────────────────────────────────
  ctx.font         = 'bold 10px system-ui, sans-serif';
  ctx.fillStyle    = color;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(node.label, x, y - paraH / 2 - 6);

  if (!node.innerIdentity?.length) { ctx.restore(); return; }

  // ── Mid-zoom inner rendering ──────────────────────────────────────────────
  if (scale >= 0.45 && scale < 2.0) {

    // Character bible header bar
    const bibleItem = node.innerIdentity.find(i => i.pillar === 'character_bible');
    if (bibleItem) {
      const barW = paraW * 0.85;
      const barH = 10;
      const bx   = x + bibleItem.dx;
      const by   = y + bibleItem.dy;
      ctx.fillStyle   = darkMode ? hexToRgba(PILLAR_COLORS.character_bible, 0.35) : hexToRgba(PILLAR_COLORS.character_bible, 0.20);
      ctx.strokeStyle = hexToRgba(PILLAR_COLORS.character_bible, 0.7);
      ctx.lineWidth   = 0.8;
      ctx.beginPath();
      ctx.roundRect(bx - barW / 2, by - barH / 2, barW, barH, 3);
      ctx.fill();
      ctx.stroke();
      if (scale >= 0.55) {
        ctx.font         = '7px system-ui, sans-serif';
        ctx.fillStyle    = darkMode ? '#d1d5db' : '#374151';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        const short = bibleItem.label.length > 32 ? bibleItem.label.slice(0, 32) + '…' : bibleItem.label;
        ctx.fillText(short, bx, by);
      }
    }

    // Pillar column headers
    const pillars: Array<{ key: 'tethic' | 'pattern' | 'antipattern'; label: string; colX: number }> = [
      { key: 'tethic',      label: 'TETHICS',   colX: x - paraW / 4 },
      { key: 'pattern',     label: 'PATTERNS',  colX: x },
      { key: 'antipattern', label: 'ANTI',       colX: x + paraW / 4 },
    ];

    if (scale >= 0.55) {
      pillars.forEach(({ key, label, colX }) => {
        const pillarColor = PILLAR_COLORS[key];
        ctx.font         = 'bold 6px system-ui, sans-serif';
        ctx.fillStyle    = hexToRgba(pillarColor, 0.8);
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(label, colX, y - paraH / 2 + 22);

        // Thin column separator line
        ctx.strokeStyle = hexToRgba(pillarColor, 0.2);
        ctx.lineWidth   = 0.5;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(colX - paraW / 8, y - paraH / 2 + 24);
        ctx.lineTo(colX - paraW / 8, y + paraH / 2 - 6);
        ctx.stroke();
        ctx.setLineDash([]);
      });
    }

    // Per-pillar items
    node.innerIdentity
      .filter(i => i.pillar !== 'character_bible')
      .forEach(item => {
        const pillarColor = PILLAR_COLORS[item.pillar as keyof typeof PILLAR_COLORS] ?? color;
        const px = x + item.dx;
        const py = y + item.dy;
        const pillW = Math.max(10, Math.min(44, scale * 52));
        const pillH = Math.max(4, Math.min(8, scale * 10));

        ctx.fillStyle   = darkMode ? hexToRgba(pillarColor, 0.28) : hexToRgba(pillarColor, 0.15);
        ctx.strokeStyle = hexToRgba(pillarColor, 0.6);
        ctx.lineWidth   = 0.7;
        ctx.beginPath();
        ctx.roundRect(px - pillW / 2, py - pillH / 2, pillW, pillH, 2);
        ctx.fill();
        ctx.stroke();

        if (scale >= 0.65) {
          ctx.font         = '6px system-ui, sans-serif';
          ctx.fillStyle    = darkMode ? '#d1d5db' : '#374151';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const short = item.label.length > 14 ? item.label.slice(0, 14) + '…' : item.label;
          ctx.fillText(short, px, py);
        }
      });

    // ── Intra-cluster cross-link arcs ─────────────────────────────────────
    if (scale >= 0.75) {
      const itemById = new Map(node.innerIdentity.map(i => [i.id, i]));

      node.innerIdentity.forEach(src => {
        const srcX = x + src.dx;
        const srcY = y + src.dy;

        // Tethic → antipattern reinforcement arcs (green-ish)
        (src.reinforces || []).forEach(targetId => {
          const tgt = itemById.get(targetId);
          if (!tgt) return;
          const tgtX = x + tgt.dx;
          const tgtY = y + tgt.dy;
          const mx   = (srcX + tgtX) / 2;
          const my   = (srcY + tgtY) / 2 - 12;
          ctx.save();
          ctx.strokeStyle = 'rgba(34,197,94,0.35)';
          ctx.lineWidth   = 0.8;
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.moveTo(srcX, srcY);
          ctx.quadraticCurveTo(mx, my, tgtX, tgtY);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        });

        // Pattern → antipattern conflict arcs (amber)
        (src.conflicts || []).forEach(targetId => {
          const tgt = itemById.get(targetId);
          if (!tgt) return;
          const tgtX = x + tgt.dx;
          const tgtY = y + tgt.dy;
          const mx   = (srcX + tgtX) / 2;
          const my   = (srcY + tgtY) / 2 + 12;
          ctx.save();
          ctx.strokeStyle = 'rgba(245,158,11,0.30)';
          ctx.lineWidth   = 0.8;
          ctx.setLineDash([1, 4]);
          ctx.beginPath();
          ctx.moveTo(srcX, srcY);
          ctx.quadraticCurveTo(mx, my, tgtX, tgtY);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        });
      });
    }
  }

  ctx.restore();
}

function drawCapabilityCluster(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, radius, color } = node;

  ctx.save();
  ctx.strokeStyle = selected ? '#ffffff' : color;
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.fillStyle = darkMode ? hexToRgba(color, 0.12) : hexToRgba(color, 0.08);
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Cluster label above ring
  ctx.font = 'bold 10px system-ui, sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(node.label, x, y - radius - 4);

  if (!node.innerCapabilities?.length) { ctx.restore(); return; }

  // Mid-zoom: score-arc rings + labels (below LOD_NODES, above 0.45)
  if (scale >= 0.45 && scale < 2.0) {
    const DOT_R = Math.max(3, Math.min(7, scale * 8));

    node.innerCapabilities.forEach((ic) => {
      const px = x + ic.dx;
      const py = y + ic.dy;
      const entry = ic.data;
      const entryColor = getDomainColorCanvas(entry.domain);
      const pct = Math.max(0, Math.min(10, entry.score ?? 0)) / 10;

      // Outer ring track
      ctx.strokeStyle = darkMode ? '#374151' : '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px, py, DOT_R, 0, Math.PI * 2);
      ctx.stroke();

      // Score arc
      ctx.strokeStyle = entryColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px, py, DOT_R, -Math.PI / 2, -Math.PI / 2 + pct * Math.PI * 2);
      ctx.stroke();

      // Center fill — score-opacity tinted
      const fillAlpha = 0.25 + pct * 0.55;
      ctx.fillStyle = hexToRgba(entryColor, fillAlpha);
      ctx.beginPath();
      ctx.arc(px, py, DOT_R - 2, 0, Math.PI * 2);
      ctx.fill();

      // Score number inside dot (only when large enough)
      if (DOT_R >= 5) {
        ctx.font = `bold ${Math.round(DOT_R * 1.1)}px system-ui, sans-serif`;
        ctx.fillStyle = darkMode ? '#f3f4f6' : '#111827';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(entry.score), px, py);
      }

      // Label below dot
      if (scale >= 0.65) {
        ctx.font = '7px system-ui, sans-serif';
        ctx.fillStyle = darkMode ? '#d1d5db' : '#374151';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const short = ic.label.length > 12 ? ic.label.slice(0, 12) + '…' : ic.label;
        ctx.fillText(short, px, py + DOT_R + 2);
      }

      // Domain accent dot (top-right of ring)
      if (scale >= 0.75) {
        ctx.fillStyle = entryColor;
        ctx.beginPath();
        ctx.arc(px + DOT_R * 0.7, py - DOT_R * 0.7, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // Average score bar at bottom of cluster
    if (scale >= 0.6) {
      const scores = node.innerCapabilities.map(ic => ic.data.score ?? 0);
      const avg = scores.reduce((a: number, b: number) => a + b, 0) / scores.length;
      const avgPct = avg / 10;

      const barW = radius * 1.1;
      const barH = 5;
      const bx = x - barW / 2;
      const by = y + radius - 14;

      // Track
      ctx.fillStyle = darkMode ? '#374151' : '#e5e7eb';
      ctx.beginPath();
      ctx.roundRect(bx, by, barW, barH, 2);
      ctx.fill();

      // Fill
      ctx.fillStyle = hexToRgba(color, 0.85);
      ctx.beginPath();
      ctx.roundRect(bx, by, barW * avgPct, barH, 2);
      ctx.fill();

      // Label
      ctx.font = '7px system-ui, sans-serif';
      ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(`avg ${avg.toFixed(1)}/10`, x, by + barH + 2);
    }
  }

  ctx.restore();
}

const CONTROL_TYPE_COLORS: Record<string, string> = {
  slider: '#a855f7',
  toggle: '#8b5cf6',
  select: '#7c3aed',
  text:   '#6d28d9',
};

const CONTROL_TYPE_GLYPHS: Record<string, string> = {
  slider: '━',
  toggle: '◉',
  select: '▾',
  text:   'T',
};

function drawControlsCluster(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, radius, color } = node;

  ctx.save();

  // ── Outer circle ──────────────────────────────────────────────────────────
  ctx.strokeStyle = selected ? '#ffffff' : color;
  ctx.lineWidth   = selected ? 2.5 : 1.5;
  ctx.fillStyle   = darkMode ? hexToRgba(color, 0.12) : hexToRgba(color, 0.08);
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // ── Cluster label above ring ──────────────────────────────────────────────
  ctx.font         = 'bold 10px system-ui, sans-serif';
  ctx.fillStyle    = color;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(node.label, x, y - radius - 4);

  if (!node.innerControls?.length) { ctx.restore(); return; }

  // ── Mid-zoom inner rendering ──────────────────────────────────────────────
  if (scale >= 0.45 && scale < 2.0) {
    const clusterData = node.data as { controls: any[]; activeCols: string[]; colSpacing: number; totalW: number };
    const activeCols  = clusterData?.activeCols ?? [];
    const COL_SPACING = clusterData?.colSpacing ?? 44;
    const totalW      = clusterData?.totalW ?? 0;

    // Column header glyphs
    if (scale >= 0.55) {
      activeCols.forEach((colType, colIdx) => {
        const colX      = x + (-totalW / 2 + colIdx * COL_SPACING);
        const typeColor = CONTROL_TYPE_COLORS[colType] ?? color;
        const glyph     = CONTROL_TYPE_GLYPHS[colType] ?? '?';

        ctx.font         = 'bold 8px system-ui, sans-serif';
        ctx.fillStyle    = hexToRgba(typeColor, 0.9);
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(glyph, colX, y - radius + 18);

        // Thin column separator
        ctx.strokeStyle = hexToRgba(typeColor, 0.18);
        ctx.lineWidth   = 0.5;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(colX - COL_SPACING / 2 + 4, y - radius + 20);
        ctx.lineTo(colX - COL_SPACING / 2 + 4, y + radius - 6);
        ctx.stroke();
        ctx.setLineDash([]);
      });
    }

    // Per-control pills
    const PILL_W = Math.max(14, Math.min(40, scale * 46));
    const PILL_H = Math.max(5,  Math.min(10, scale * 12));

    node.innerControls.forEach((ic) => {
      const px        = x + ic.dx;
      const py        = y + ic.dy;
      const ctrl      = ic.data;
      const typeColor = CONTROL_TYPE_COLORS[ctrl.type] ?? color;
      const isNonDefault = ctrl.current_value !== ctrl.default_value;

      // Pill background
      ctx.fillStyle   = darkMode ? hexToRgba(typeColor, 0.28) : hexToRgba(typeColor, 0.14);
      ctx.strokeStyle = hexToRgba(typeColor, 0.6);
      ctx.lineWidth   = 0.7;
      ctx.beginPath();
      ctx.roundRect(px - PILL_W / 2, py - PILL_H / 2, PILL_W, PILL_H, 2);
      ctx.fill();
      ctx.stroke();

      // Slider value fill bar (inside pill)
      if (ctrl.type === 'slider' && typeof ctrl.current_value === 'number') {
        const maxVal = ctrl.max ?? 1;
        const minVal = ctrl.min ?? 0;
        const pct    = Math.max(0, Math.min(1, (ctrl.current_value - minVal) / (maxVal - minVal)));
        const barW   = (PILL_W - 4) * pct;
        if (barW > 0) {
          ctx.fillStyle = hexToRgba(typeColor, isNonDefault ? 0.7 : 0.4);
          ctx.beginPath();
          ctx.roundRect(px - PILL_W / 2 + 2, py - PILL_H / 2 + 2, barW, PILL_H - 4, 1);
          ctx.fill();
        }
      }

      // Toggle ON/OFF dot
      if (ctrl.type === 'toggle') {
        const dotColor = ctrl.current_value ? '#22c55e' : '#6b7280';
        ctx.fillStyle  = dotColor;
        ctx.beginPath();
        ctx.arc(px - PILL_W / 2 + 4, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Label — only at higher zoom
      if (scale >= 0.65) {
        ctx.font         = '6px system-ui, sans-serif';
        ctx.fillStyle    = darkMode ? '#d1d5db' : '#374151';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        const short = ic.label.length > 12 ? ic.label.slice(0, 12) + '…' : ic.label;
        ctx.fillText(short, px, py);
      }

      // Amber dot top-right for non-default
      if (isNonDefault) {
        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.arc(px + PILL_W / 2 - 2, py - PILL_H / 2 + 2, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  }

  ctx.restore();
}

function drawLineageCluster(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, radius, color } = node;

  ctx.save();

  // ── Outer hexagon ─────────────────────────────────────────────────────────
  ctx.strokeStyle = selected ? '#ffffff' : color;
  ctx.lineWidth   = selected ? 2.5 : 1.5;
  ctx.fillStyle   = darkMode ? hexToRgba(color, 0.12) : hexToRgba(color, 0.08);
  ctx.setLineDash([4, 3]);
  drawHexagon(ctx, x, y, radius);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // ── Cluster label above ───────────────────────────────────────────────────
  ctx.font         = 'bold 10px system-ui, sans-serif';
  ctx.fillStyle    = color;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(node.label, x, y - radius - 4);

  // ── Remix depth badge (top-right of hexagon) ──────────────────────────────
  const remixDepth = node.remixDepth ?? 0;
  if (remixDepth > 0) {
    const bx = x + radius * 0.6;
    const by = y - radius * 0.6;
    ctx.fillStyle = '#6366f1';
    ctx.beginPath();
    ctx.arc(bx, by, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.font         = 'bold 7px system-ui, sans-serif';
    ctx.fillStyle    = '#ffffff';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`🔁${remixDepth}`, bx, by);
  }

  if (!node.innerLineage?.length) { ctx.restore(); return; }

  // ── Mid-zoom inner rendering ──────────────────────────────────────────────
  if (scale >= 0.45 && scale < 2.0) {

    // Origin bar
    const originItem = node.innerLineage.find(i => i.section === 'origin');
    if (originItem) {
      const barW = radius * 1.1;
      const barH = 9;
      const ox   = x + originItem.dx;
      const oy   = y + originItem.dy;
      ctx.fillStyle   = darkMode ? hexToRgba('#6366f1', 0.35) : hexToRgba('#6366f1', 0.20);
      ctx.strokeStyle = hexToRgba('#6366f1', 0.7);
      ctx.lineWidth   = 0.8;
      ctx.beginPath();
      ctx.roundRect(ox - barW / 2, oy - barH / 2, barW, barH, 3);
      ctx.fill();
      ctx.stroke();
      if (scale >= 0.55) {
        ctx.font         = '7px system-ui, sans-serif';
        ctx.fillStyle    = darkMode ? '#c7d2fe' : '#4338ca';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(originItem.label, ox, oy);
      }
    }

    // Tag pills — colored by category
    node.innerLineage
      .filter(i => i.section === 'tag')
      .forEach(item => {
        const tagColor = item.tagColor ?? color;
        const px = x + item.dx;
        const py = y + item.dy;
        const pillW = Math.max(12, Math.min(38, scale * 44));
        const pillH = Math.max(4, Math.min(8, scale * 10));

        ctx.fillStyle   = darkMode ? hexToRgba(tagColor, 0.30) : hexToRgba(tagColor, 0.15);
        ctx.strokeStyle = hexToRgba(tagColor, 0.65);
        ctx.lineWidth   = 0.7;
        ctx.beginPath();
        ctx.roundRect(px - pillW / 2, py - pillH / 2, pillW, pillH, 3);
        ctx.fill();
        ctx.stroke();

        if (scale >= 0.65) {
          ctx.font         = '6px system-ui, sans-serif';
          ctx.fillStyle    = darkMode ? '#d1d5db' : '#374151';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const short = item.label.length > 12 ? item.label.slice(0, 12) + '…' : item.label;
          ctx.fillText(short, px, py);
        }
      });

    // Suggested name pills — vertical list
    node.innerLineage
      .filter(i => i.section === 'suggested_name')
      .forEach(item => {
        const px = x + item.dx;
        const py = y + item.dy;
        const pillW = Math.max(14, Math.min(44, scale * 50));
        const pillH = Math.max(4, Math.min(8, scale * 10));

        ctx.fillStyle   = darkMode ? hexToRgba('#9ca3af', 0.20) : hexToRgba('#9ca3af', 0.12);
        ctx.strokeStyle = hexToRgba('#9ca3af', 0.50);
        ctx.lineWidth   = 0.6;
        ctx.beginPath();
        ctx.roundRect(px - pillW / 2, py - pillH / 2, pillW, pillH, 3);
        ctx.fill();
        ctx.stroke();

        if (scale >= 0.65) {
          ctx.font         = '6px system-ui, sans-serif';
          ctx.fillStyle    = darkMode ? '#9ca3af' : '#6b7280';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const short = item.label.length > 14 ? item.label.slice(0, 14) + '…' : item.label;
          ctx.fillText(short, px, py);
        }
      });
  }

  ctx.restore();
}

function drawSystemPromptNode(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  needsRecompile: boolean,
  darkMode: boolean,
) {
  const { x, y, radius: r, color } = node;
  const isStale = needsRecompile;
  const strokeColor = isStale ? '#f59e0b' : color;

  ctx.save();

  // ── Outer glow ring ───────────────────────────────────────────────────────
  if (selected || isStale) {
    ctx.shadowColor = isStale ? '#f59e0b' : color;
    ctx.shadowBlur = 18;
  }

  // ── Double hexagon: outer halo ────────────────────────────────────────────
  ctx.strokeStyle = hexToRgba(strokeColor, 0.3);
  ctx.lineWidth = 6;
  ctx.fillStyle = 'transparent';
  drawHexagon(ctx, x, y, r + 8);
  ctx.stroke();

  // ── Inner hexagon fill ────────────────────────────────────────────────────
  ctx.shadowBlur = 0;
  ctx.fillStyle = darkMode ? hexToRgba(color, 0.28) : hexToRgba(color, 0.18);
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = selected ? 3 : 2;
  drawHexagon(ctx, x, y, r);
  ctx.fill();
  ctx.stroke();

  // ── Stale amber pulse ring ────────────────────────────────────────────────
  if (isStale) {
    ctx.strokeStyle = 'rgba(245,158,11,0.45)';
    ctx.lineWidth = 4;
    drawHexagon(ctx, x, y, r + 14);
    ctx.stroke();
  }

  // ── Mid-zoom inner content ────────────────────────────────────────────────
  if (scale >= 0.45) {
    const prompt: string = node.data || '';
    const tokenEstimate = Math.round(prompt.length / 4);
    const wordCount = prompt.trim().split(/\s+/).filter(Boolean).length;

    // Token count badge (center)
    ctx.font = 'bold 9px system-ui, sans-serif';
    ctx.fillStyle = isStale ? '#f59e0b' : color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      tokenEstimate > 0 ? `~${tokenEstimate.toLocaleString()}` : 'empty',
      x, y - 5,
    );

    // "tokens" sub-label
    ctx.font = '7px system-ui, sans-serif';
    ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
    ctx.fillText('tokens', x, y + 6);

    // Word count strip below
    if (scale >= 0.6 && wordCount > 0) {
      ctx.font = '7px system-ui, sans-serif';
      ctx.fillStyle = darkMode ? '#6b7280' : '#9ca3af';
      ctx.fillText(`${wordCount.toLocaleString()} words`, x, y + 16);
    }

    // Stale warning glyph
    if (isStale && scale >= 0.55) {
      ctx.font = 'bold 8px system-ui, sans-serif';
      ctx.fillStyle = '#f59e0b';
      ctx.fillText('⚠ stale', x, y - r + 14);
    }
  }

  ctx.restore();
}

function drawControllerPromptNode(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  darkMode: boolean,
) {
  const { x, y, radius: r, color } = node;

  ctx.save();

  if (selected) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
  }

  // ── Hexagon fill ──────────────────────────────────────────────────────────
  ctx.fillStyle = darkMode ? hexToRgba(color, 0.22) : hexToRgba(color, 0.14);
  ctx.strokeStyle = color;
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.setLineDash([4, 3]);
  drawHexagon(ctx, x, y, r);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // ── Mid-zoom inner content ────────────────────────────────────────────────
  if (scale >= 0.45) {
    const prompt: string = node.data || '';
    const tokenEstimate = Math.round(prompt.length / 4);

    ctx.font = 'bold 8px system-ui, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      tokenEstimate > 0 ? `~${tokenEstimate.toLocaleString()}` : 'empty',
      x, y - 4,
    );

    ctx.font = '7px system-ui, sans-serif';
    ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
    ctx.fillText('ctrl tok', x, y + 5);

    // Gear glyph at top of hex
    if (scale >= 0.55) {
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = hexToRgba(color, 0.7);
      ctx.fillText('⚙', x, y - r + 12);
    }
  }

  ctx.restore();
}

function drawNode(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  selected: boolean,
  scale: number,
  needsRecompile: boolean,
  darkMode: boolean,
) {
  const { x, y, radius, color, type } = node;
  const r = radius;

  ctx.save();

  // Selection glow
  if (selected) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
  }

  // Stale amber border for system_prompt
  const isStale = type === 'system_prompt' && needsRecompile;
  const strokeColor = isStale ? '#f59e0b' : color;
  const fillColor = darkMode ? hexToRgba(color, 0.25) : hexToRgba(color, 0.15);

  ctx.fillStyle = fillColor;
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = selected ? 2.5 : 1.5;

  switch (type) {
    case 'character_bible':
      ctx.beginPath();
      ctx.roundRect(x - r * 1.4, y - r * 0.8, r * 2.8, r * 1.6, 6);
      break;
    case 'tethic':
    case 'system_prompt':
    case 'controller_prompt':
      drawHexagon(ctx, x, y, r);
      break;
    case 'pattern':
      drawDiamond(ctx, x, y, r);
      break;
    case 'antipattern':
      drawTriangleDown(ctx, x, y, r);
      break;
    default:
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      break;
  }

  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Stale pulse ring
  if (isStale) {
    ctx.save();
    ctx.strokeStyle = 'rgba(245,158,11,0.4)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, r + 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Probe pass/fail dot
  if (type === 'probe') {
    const probe = node.data;
    const dotColor =
      probe.last_passed === true  ? '#22c55e' :
      probe.last_passed === false ? '#ef4444' : '#6b7280';
    ctx.save();
    ctx.fillStyle = dotColor;
    ctx.beginPath();
    ctx.arc(x + r * 0.6, y - r * 0.6, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  darkMode: boolean,
) {
  const { x, y, radius, label } = node;
  const maxLen = 18;
  const text = label.length > maxLen ? label.slice(0, maxLen) + '…' : label;

  ctx.save();
  ctx.font = '10px system-ui, sans-serif';
  ctx.fillStyle = darkMode ? '#d1d5db' : '#374151';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(text, x, y + radius + 3);
  ctx.restore();
}

function drawClusterBubble(
  ctx: CanvasRenderingContext2D,
  clusterId: string,
  nodes: MapNode[],
  darkMode: boolean,
  clusterPositions: Record<string, { x: number; y: number; color: string; label: string }> = CLUSTER_POSITIONS,
) {
  const pos = clusterPositions[clusterId];
  if (!pos) return;

  const r = 70;
  ctx.save();
  ctx.fillStyle = darkMode ? hexToRgba(pos.color, 0.12) : hexToRgba(pos.color, 0.08);
  ctx.strokeStyle = hexToRgba(pos.color, 0.4);
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Label
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.fillStyle = pos.color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(pos.label, pos.x, pos.y - r - 12);

  // Count badge
  ctx.font = '10px system-ui, sans-serif';
  ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
  ctx.fillText(`${nodes.length}`, pos.x, pos.y);
  ctx.restore();
}

function drawClusterHalos(
  ctx: CanvasRenderingContext2D,
  nodes: MapNode[],
  canvasW: number,
  canvasH: number,
  darkMode: boolean,
) {
  // Group nodes by cluster
  const groups = new Map<string, MapNode[]>();
  nodes.forEach((n) => {
    const arr = groups.get(n.clusterId) ?? [];
    arr.push(n);
    groups.set(n.clusterId, arr);
  });

  const clusterPositions = computeClusterPositions(canvasW, canvasH);

  groups.forEach((clusterNodes, clusterId) => {
    const pos = clusterPositions[clusterId];
    if (!pos || clusterNodes.length === 0) return;

    // Compute bounding circle from actual settled positions
    let maxDist = 0;
    clusterNodes.forEach((n) => {
      const dx = n.x - pos.x;
      const dy = n.y - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy) + n.radius;
      if (dist > maxDist) maxDist = dist;
    });
    const haloR = Math.max(maxDist + 18, 50);

    ctx.save();
    ctx.fillStyle = hexToRgba(pos.color, darkMode ? 0.07 : 0.05);
    ctx.strokeStyle = hexToRgba(pos.color, darkMode ? 0.18 : 0.12);
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, haloR, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);

    // Cluster label at top of halo
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.fillStyle = hexToRgba(pos.color, 0.7);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(pos.label, pos.x, pos.y - haloR - 4);
    ctx.restore();
  });
}

function drawEdges(
  ctx: CanvasRenderingContext2D,
  nodes: MapNode[],
  needsRecompile: boolean,
  darkMode: boolean,
) {
  const byType = new Map<string, MapNode[]>();
  nodes.forEach((n) => {
    const arr = byType.get(n.type) ?? [];
    arr.push(n);
    byType.set(n.type, arr);
  });

  const systemNodes = byType.get('system_prompt') ?? [];
  if (!systemNodes.length) return;
  const target = systemNodes[0];

  EDGES.forEach(({ from, to, style, width }) => {
    const sources = byType.get(from) ?? [];
    sources.forEach((src) => {
      const edgeColor = needsRecompile && to === 'system_prompt'
        ? 'rgba(245,158,11,0.5)'
        : hexToRgba(src.color, 0.5);

      ctx.save();
      ctx.strokeStyle = edgeColor;
      ctx.lineWidth = width;

      if (style === 'dashed') ctx.setLineDash([5, 4]);
      else if (style === 'dotted') ctx.setLineDash([2, 3]);
      else ctx.setLineDash([]);

      // Bezier curve
      const mx = (src.x + target.x) / 2;
      const my = (src.y + target.y) / 2 - 30;
      ctx.beginPath();
      ctx.moveTo(src.x, src.y);
      ctx.quadraticCurveTo(mx, my, target.x, target.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    });
  });
}

function drawMinimap(
  ctx: CanvasRenderingContext2D,
  nodes: MapNode[],
  transform: Transform,
  canvasW: number,
  canvasH: number,
  darkMode: boolean,
) {
  const mmW = 120;
  const mmH = 80;
  const mmX = canvasW - mmW - 8;
  const mmY = canvasH - mmH - 8;
  const pad = 10;

  // Background
  ctx.save();
  ctx.fillStyle = darkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)';
  ctx.strokeStyle = darkMode ? '#374151' : '#d1d5db';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(mmX, mmY, mmW, mmH, 4);
  ctx.fill();
  ctx.stroke();

  if (!nodes.length) { ctx.restore(); return; }

  // Compute bounds of all nodes
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs) - pad;
  const maxX = Math.max(...xs) + pad;
  const minY = Math.min(...ys) - pad;
  const maxY = Math.max(...ys) + pad;
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;

  const toMM = (wx: number, wy: number) => ({
    x: mmX + ((wx - minX) / rangeX) * mmW,
    y: mmY + ((wy - minY) / rangeY) * mmH,
  });

  // Draw nodes as tiny dots
  nodes.forEach((n) => {
    const { x, y } = toMM(n.x, n.y);
    ctx.fillStyle = n.color;
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI * 2);
    ctx.fill();
  });

  // Viewport rect
  const vpX = -transform.x / transform.scale;
  const vpY = -transform.y / transform.scale;
  const vpW = canvasW / transform.scale;
  const vpH = canvasH / transform.scale;

  const tl = toMM(vpX, vpY);
  const br = toMM(vpX + vpW, vpY + vpH);
  ctx.strokeStyle = '#3b82f6';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);

  ctx.restore();
}

// ── Hit test ──────────────────────────────────────────────────────────────────
function hitTest(
  nodes: MapNode[],
  wx: number,
  wy: number,
): MapNode | null {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (n.type === 'identity_cluster') {
      const hw = (n.paraW ?? 180) / 2 + (n.paraSkew ?? 16) + 4;
      const hh = (n.paraH ?? 130) / 2 + 4;
      if (Math.abs(wx - n.x) <= hw && Math.abs(wy - n.y) <= hh) return n;
    } else {
      const dx = wx - n.x;
      const dy = wy - n.y;
      if (Math.sqrt(dx * dx + dy * dy) <= n.radius + 4) return n;
    }
  }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────
const MapCanvas: React.FC<MapCanvasProps> = ({
  nodes,
  transform,
  selectedNodeId,
  needsRecompile,
  darkMode,
  onNodeDoubleClick,
  onNodeClick,
  onNodeHover,
  onTransformChange,
  onCanvasSize,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const drawRef = useRef<() => void>(() => {});
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const onCanvasSizeRef = useRef(onCanvasSize);
  const transformRef = useRef(transform);
  const onTransformChangeRef = useRef(onTransformChange);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    ctx.save();
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.scale, transform.scale);

    const scale = transform.scale;

    if (scale < LOD_CLUSTER) {
      const clusterPositions = computeClusterPositions(W, H);
      const clusterIds = [...new Set(nodes.map((n) => n.clusterId))];
      clusterIds.forEach((cid) => {
        const clusterNodes = nodes.filter((n) => n.clusterId === cid);
        drawClusterBubble(ctx, cid, clusterNodes, darkMode, clusterPositions);
      });
    } else {
      // Dynamic halos visible at all non-cluster zoom levels
      drawClusterHalos(ctx, nodes, W, H, darkMode);

      // Draw edges
      drawEdges(ctx, nodes, needsRecompile, darkMode);

      // Draw nodes
      nodes.forEach((node) => {
        if (node.type === 'identity_cluster') {
          drawIdentityCluster(ctx, node, node.id === selectedNodeId, scale, darkMode);
        } else if (node.type === 'lineage_cluster') {
          drawLineageCluster(ctx, node, node.id === selectedNodeId, scale, darkMode);
        } else if (node.type === 'probe_cluster') {
          drawProbeCluster(ctx, node, node.id === selectedNodeId, scale, darkMode);
        } else if (node.type === 'capability_cluster') {
          drawCapabilityCluster(ctx, node, node.id === selectedNodeId, scale, darkMode);
        } else if (node.type === 'controls_cluster') {
          drawControlsCluster(ctx, node, node.id === selectedNodeId, scale, darkMode);
        } else if (node.type === 'system_prompt') {
          drawSystemPromptNode(ctx, node, node.id === selectedNodeId, scale, needsRecompile, darkMode);
          if (scale >= LOD_NODES) drawLabel(ctx, node, darkMode);
        } else if (node.type === 'controller_prompt') {
          drawControllerPromptNode(ctx, node, node.id === selectedNodeId, scale, darkMode);
          if (scale >= LOD_NODES) drawLabel(ctx, node, darkMode);
        } else {
          drawNode(ctx, node, node.id === selectedNodeId, scale, needsRecompile, darkMode);
          if (scale >= LOD_NODES) {
            drawLabel(ctx, node, darkMode);
          }
        }
      });
    }

    ctx.restore();

    // Minimap always drawn in screen space
    drawMinimap(ctx, nodes, transform, W, H, darkMode);
  }, [nodes, transform, selectedNodeId, needsRecompile, darkMode]);

  // Keep all refs current so effects don't need props/state as deps.
  useEffect(() => {
    drawRef.current = draw;
  });

  useEffect(() => {
    onCanvasSizeRef.current = onCanvasSize;
  });

  useEffect(() => {
    transformRef.current = transform;
  });

  useEffect(() => {
    onTransformChangeRef.current = onTransformChange;
  });

  // RAF loop — starts once on mount, never restarts
  useEffect(() => {
    const loop = () => {
      drawRef.current();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // empty deps — intentional

  // Resize observer — also reports canvas size for layout recalculation.
  // Uses a ref for onCanvasSize so this effect never needs to re-run.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      onCanvasSizeRef.current?.(canvas.offsetWidth, canvas.offsetHeight);
    });
    ro.observe(canvas);
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    onCanvasSizeRef.current?.(canvas.offsetWidth, canvas.offsetHeight);
    return () => ro.disconnect();
  }, []); // empty deps — intentional, uses ref

  // Non-passive wheel listener so preventDefault() actually works.
  // React attaches synthetic onWheel as passive in modern browsers,
  // which silently ignores preventDefault() and lets the page scroll.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const current = transformRef.current;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.1, Math.min(6, current.scale * delta));
      onTransformChangeRef.current({
        x: cx - (cx - current.x) * (newScale / current.scale),
        y: cy - (cy - current.y) * (newScale / current.scale),
        scale: newScale,
      });
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []); // empty deps — intentional, uses refs

  // ── Interaction ───────────────────────────────────────────────────────────
  const toWorld = (cx: number, cy: number) => ({
    wx: (cx - transform.x) / transform.scale,
    wy: (cy - transform.y) / transform.scale,
  });

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) {
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      lastPos.current = { x: e.clientX, y: e.clientY };
      onTransformChange({ ...transform, x: transform.x + dx, y: transform.y + dy });
      return;
    }
    if (onNodeHover) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const { wx, wy } = toWorld(e.clientX - rect.left, e.clientY - rect.top);
      const hit = hitTest(nodes, wx, wy);
      onNodeHover(hit);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseLeaveCanvas = () => {
    isDragging.current = false;
    onNodeHover?.(null);
  };

  const handleClick = (e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const { wx, wy } = toWorld(e.clientX - rect.left, e.clientY - rect.top);
    const hit = hitTest(nodes, wx, wy);
    if (hit) onNodeClick(hit);
  };

  const handleDblClick = (e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const { wx, wy } = toWorld(e.clientX - rect.left, e.clientY - rect.top);
    const hit = hitTest(nodes, wx, wy);
    if (hit) onNodeDoubleClick(hit);
  };

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block', cursor: isDragging.current ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeaveCanvas}
      onClick={handleClick}
      onDoubleClick={handleDblClick}
    />
  );
};

export default MapCanvas;
